import React, { useRef, useState } from 'react';
import axios from 'axios';
import { getFunnel } from '../utils/tracking';
import { Form, Button } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { useEffect } from 'react';
import RegisterSocial from './RegisterSocial';
import injectCollectJS from '../injectCollect';
import { cardFieldsComplete } from '../utils/cardValidity';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { Modal } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';

const Checkout = ({ profile, planName, isAnnual, firstPackageInterval, upsellChoices, upsellStep, email: accountEmail, onChangeEmail, isExistingUser }) => {
	const navigate = useNavigate();
	const L = useLanguage();
	const [isLoading, setIsLoading] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [selectedCard, setSelectedCard] = useState('addCard');
	const [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
	const [showAddProfile, setShowAddProfile] = useState(false);

	const [amount, setAmount] = useState('0.01');
	const [paymentStatus, setPaymentStatus] = useState('');
	const [functionError, setFunctionError] = useState('');
	const [expError, setExpError] = useState('');
	// Auto-renew is on by default; turning it off buys a single billing period.
	const [recurring, setRecurring] = useState(true);
	// CollectJS captures its callback when the wallet buttons are initialized, so
	// calling it directly would freeze the state of that render — a visitor who
	// turned auto-renew off and paid with Apple Pay would still be subscribed.
	// Route through a ref that always holds the latest handler.
	const walletSubmitRef = useRef();
	const [isReinitializingCollect, setIsReinitializingCollect] = useState(false);
	const cardRef = useRef();
	const expRef = useRef();
	const cvvRef = useRef();
	const mountedRef = useRef(true);
	const paymentInfoFired = useRef(false);

	// AddPaymentInfo means the visitor supplied a usable payment method: every
	// card field complete and valid, or an authorized wallet payment. Fires once
	// per checkout regardless of which path got there.
	const fireAddPaymentInfo = () => {
		if (paymentInfoFired.current) return;
		paymentInfoFired.current = true;
		axios.post("https://api.viralgpt.com/pixel", {
			email: profile.email,
			event: "AddPaymentInfo",
			funnel: getFunnel(),
		}, { withCredentials: true }).catch(() => {});
	};

	// Any of the three fields can be the one that completes the set, so all three
	// re-check on every keystroke.
	const checkCardFields = () => {
		if (paymentInfoFired.current) return;
		if (cardFieldsComplete(cardRef.current?.value, expRef.current?.value, cvvRef.current?.value)) {
			fireAddPaymentInfo();
		}
	};

	const validateExpiration = (value) => {
		if (!value || value.length < 5) {
			setExpError('');
			return;
		}
		const [monthStr, yearStr] = value.split('/');
		const month = parseInt(monthStr, 10);
		const year = parseInt(yearStr, 10);

		if (month < 1 || month > 12) {
			setExpError(L.registerCheckout.invalidMonth);
			return;
		}

		const now = new Date();
		const currentMonth = now.getMonth() + 1;
		const currentYear = now.getFullYear() % 100;

		if (year < currentYear || (year === currentYear && month < currentMonth)) {
			setExpError(L.registerCheckout.cardExpired);
			return;
		}

		setExpError('');
	};

	const prices = {
		"pro": {
			"monthly": "199",
			"annual": "1188",
			"annual_monthly": "99"
		},
		"basic": {
			"monthly": "59",
			"annual": "348",
			"annual_monthly": "29"
		},
		"starter": {
			"monthly": "29",
			"annual": "168",
			"annual_monthly": "14"
		}
	}

	useEffect(() => {
		var price = parseFloat(prices[planName][isAnnual ? "annual" : "monthly"]);

		if (upsellChoices.includes("likes")) {
			price += isAnnual ? 12 * 12 : 24;
		}

		if (upsellChoices.includes("views")) {
			price += isAnnual ? 11 * 12 : 22;
		}

		if (upsellChoices.includes("engagement")) {
			price += isAnnual ? 9 * 12 : 17;
		}

		setAmount(price.toString());

		setIsReinitializingCollect(true);

		const timeoutId = setTimeout(() => {
			if (!mountedRef.current) return;

			try {
				injectCollectJS('https://secure.nmi.com/token/Collect.js', '9t4q59-U47k7W-Cj7Ty9-MbC6Y6', (token, cardDetails) => walletSubmitRef.current(token, cardDetails), price.toString())
					.then(() => {
						if (!mountedRef.current) return;
						setIsReinitializingCollect(false);
					})
					.catch((error) => {
						if (!mountedRef.current) return;
						console.error('Error initializing CollectJS:', error);
						setIsReinitializingCollect(false);
					});
			} catch (error) {
				if (!mountedRef.current) return;
				console.error('Error calling injectCollectJS:', error);
				setIsReinitializingCollect(false);
			}
		}, 300);

		return () => {
			clearTimeout(timeoutId);
		};

	}, [planName, isAnnual, upsellChoices]);

	useEffect(() => {
		mountedRef.current = true;

		const handleError = (event) => {
			if (event.filename && event.filename.includes('nmi.com')) {
				event.preventDefault();
				console.warn('CollectJS error caught:', event.message);
				return true;
			}
		};

		window.addEventListener('error', handleError);

		return () => {
			mountedRef.current = false;
			window.removeEventListener('error', handleError);
		};
	}, []);


	const [showEmailModal, setShowEmailModal] = useState(false);
	const [email, setEmail] = useState(profile.email);

	const handleEmailChange = (e) => {
		setEmail(e.target.value);
	}

	const handleEmailSubmit = () => {
		axios.post("https://api.viralgpt.com/auth/settings", {
			email: email
		}, {
			headers: {
				Authorization: `Bearer ${localStorage.getItem("accessToken")}`
			}
		})
			.then(response => {
				setShowEmailModal(false);
				window.location.reload();
			})
			.catch(error => {
				alert(L.registerCheckout.errorUpdatingEmail);
			});
	}

	useEffect(() => {
		setEmail(profile.email);
	}, [profile]);


	const purchasePaymentToken = async (paymenttoken, cardDetails) => {
		// Wallet path: the sheet has been authorized and handed us a token, which
		// is the wallet equivalent of a completed card form.
		fireAddPaymentInfo();
		setIsLoading(true);
		setFunctionError('');
		try {
			const response = await axios.post('https://api.viralgpt.com/purchase', {
				paymenttoken: paymenttoken,
				newPlan: planName,
				newInterval: isAnnual ? "annual" : "monthly",
				upsellChoices: upsellChoices,
				recurring
			}, {
				headers: {
					'Authorization': 'Bearer ' + localStorage.getItem('accessToken')
				}
			});
			let price = parseFloat(isAnnual ? pricing[planName].annual : pricing[planName]?.monthly);

			let totalPrice = parseFloat(price);

			if (upsellChoices.includes("likes")) {
				totalPrice += isAnnual ? 12 * 12 : 24;
			}

			if (upsellChoices.includes("views")) {
				totalPrice += isAnnual ? 11 * 12 : 22;
			}

			if (upsellChoices.includes("engagement")) {
				totalPrice += isAnnual ? 9 * 12 : 17;
			}

			if (response.status === 200) {
				axios.post("https://api.viralgpt.com/pixel", {
					email: profile.email,
					event: "Purchase",
					funnel: getFunnel(),
					price: totalPrice
				}, {
					withCredentials: true
				}).catch(() => {});
				setShowSuccess(true);
			}
		} catch (err) {
			const errorMessage = err.response?.data?.error || err.message || L.registerCheckout.unknownError;
			setFunctionError(errorMessage);
		} finally {
			setIsLoading(false);
		}
		return;
	}
	walletSubmitRef.current = purchasePaymentToken;

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		setFunctionError('');

		try {
			let data;
			if (selectedCard == 'addCard' || selectedCard == 'noCard') {
				data = {
					card: cardRef.current.value,
					exp: expRef.current.value,
					cvv: cvvRef.current.value,
					newPlan: planName,
					newInterval: isAnnual ? "annual" : "monthly",
					upsellChoices: upsellChoices,
					recurring
				}
			} else {
				data = {
					cardid: selectedCard,
					newPlan: planName,
					newInterval: isAnnual ? "annual" : "monthly",
					upsellChoices: upsellChoices,
					recurring
				}
			}

			const response = await axios.post('https://api.viralgpt.com/purchase', data, {
				headers: {
					'Authorization': 'Bearer ' + localStorage.getItem('accessToken')
				}
			});

			let price = parseFloat(isAnnual ? pricing[planName].annual : pricing[planName]?.monthly);

			let totalPrice = parseFloat(price);

			if (upsellChoices.includes("likes")) {
				totalPrice += isAnnual ? 12 * 12 : 24;
			}

			if (upsellChoices.includes("views")) {
				totalPrice += isAnnual ? 11 * 12 : 22;
			}

			if (upsellChoices.includes("engagement")) {
				totalPrice += isAnnual ? 9 * 12 : 17;
			}

			if (response.status === 200) {
				axios.post("https://api.viralgpt.com/pixel", {
					email: profile.email,
					event: "Purchase",
					funnel: getFunnel(),
					price: totalPrice
				}, {
					withCredentials: true
				}).catch(() => {});
				setShowSuccess(true);
			}
		} catch (err) {
			const errorMessage = err.response?.data?.error || err.message || L.registerCheckout.unknownError;
			setFunctionError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};


	const pricing = {
		"pro": {
			"monthly": 199,
			"annual": 99 * 12
		},
		"basic": {
			"monthly": 59,
			"annual": 29 * 12
		},
		"starter": {
			"monthly": 29,
			"annual": 14 * 12
		}
	}

	const selectProfile = (uuid) => {
		axios.post("https://api.viralgpt.com/auth/select", {
			profileid: uuid
		}, {
			headers: {
				Authorization: `Bearer ${localStorage.getItem("accessToken")}`
			}
		})
			.then(response => {
				window.location.reload();
			})
			.catch(error => {
				console.error("Error selecting profile:", error);
			});
	};

	useEffect(() => {
		if (isExistingUser) return;
		const dedupeKey = `ic_fired_${planName}_${isAnnual ? "annual" : "monthly"}`;
		if (sessionStorage.getItem(dedupeKey)) return;
		sessionStorage.setItem(dedupeKey, "1");
		axios.post("https://api.viralgpt.com/pixel", {
			email: profile.email,
			event: "InitiateCheckout",
			funnel: getFunnel(),
			price: isAnnual ? pricing[planName].annual : pricing[planName]?.monthly
		}, {
			withCredentials: true
		}).catch(() => {});
	}, [isExistingUser, planName, isAnnual, profile?.email]);

	const handleDeleteProfile = (profileid) => {
		axios.post("https://api.viralgpt.com/auth/unlink", {
			profileid: profileid
		}, {
			headers: {
				Authorization: `Bearer ${localStorage.getItem("accessToken")}`
			}
		})
			.then(response => {
				window.location.reload();
			})
			.catch(error => {
				console.error("Error unlinking profile:", error);
			});
	}

	const handleSetPassword = () => {
		const password = document.getElementById('password').value;
		if (!password) {
			alert(L.registerCheckout.enterPassword);
			return;
		}

		axios.post('https://api.viralgpt.com/auth/settings', {
			oldPassword: 'no-password',
			newPassword: password
		}, {
			headers: {
				Authorization: `Bearer ${localStorage.getItem('accessToken')}`
			}
		})
			.then(response => {
				window.location.href = '/dashboard/magnet';
			})
			.catch(error => {
				console.error('Error setting password:', error);
				alert(error.response?.data?.error || L.registerCheckout.setPasswordFailed);
			});
	}

	// Plan display helpers
	const planColors = {
		starter: { bg: '#f0f0f0', text: '#555', accent: '#888' },
		basic: { bg: '#0a2540', text: '#fff', accent: '#0a2540' },
		pro: { bg: 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)', text: '#fff', accent: '#635bff' },
	};
	const currentPlanColor = planColors[planName.toLowerCase()] || planColors.basic;

	const followerRange = planName === 'starter' ? '500-1,000' : planName === 'basic' ? '2,000-3,000' : '7,500-10,000';

	const monthlyPrice = isAnnual ? Math.round(pricing[planName].annual / 12) : pricing[planName].monthly;
	const totalPrice = isAnnual ? pricing[planName].annual : pricing[planName].monthly;

	// Calculate upsell totals for order summary
	let upsellTotal = 0;
	if (upsellChoices.includes("likes")) upsellTotal += isAnnual ? 12 * 12 : 24;
	if (upsellChoices.includes("views")) upsellTotal += isAnnual ? 11 * 12 : 22;
	if (upsellChoices.includes("engagement")) upsellTotal += isAnnual ? 9 * 12 : 17;

	const getCardIcon = (number) => {
		if (number.startsWith('4')) return 'fa-brands fa-cc-visa';
		if (number.startsWith('5')) return 'fa-brands fa-cc-mastercard';
		if (number.startsWith('3')) return 'fa-brands fa-cc-amex';
		return 'fa-solid fa-credit-card';
	};

	// ─── SUCCESS STATE ───
	if (showSuccess) {
		return (
			<div className="stripe-card stripe-card--elevated stripe-card--static" style={{ maxWidth: 480, margin: '2rem auto', padding: '2.5rem 2rem', textAlign: 'center' }}>
				<Confetti className="w-full" />
				<div style={{ width: 64, height: 64, borderRadius: '50%', background: '#d4f4e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
					<i className="fa-solid fa-check" style={{ fontSize: '1.5rem', color: '#1a7f4b' }}></i>
				</div>
				<h2 className="stripe-heading stripe-heading--sm" style={{ marginBottom: 8 }}>{L.registerCheckout.allSet}</h2>
				<p className="stripe-body stripe-body--sm stripe-text-muted" style={{ marginBottom: 24 }}>
					{L.registerCheckout.planActive(planName.charAt(0).toUpperCase() + planName.slice(1))}
				</p>
				<input
					type="password"
					className="form-control mb-3"
					placeholder={L.registerCheckout.createPasswordPlaceholder}
					id="password"
					required
					style={{ padding: '0.75rem 1rem', fontSize: '1rem', borderRadius: 10, border: '1px solid #e3e8ee' }}
				/>
				<button
					onClick={handleSetPassword}
					className="stripe-btn stripe-btn--primary w-100"
					style={{ padding: '0.85rem', fontSize: '1rem' }}
				>
					{L.registerCheckout.setPasswordButton}
				</button>
			</div>
		);
	}

	// ─── MAIN CHECKOUT ───
	return (
		<div style={{ maxWidth: 480, margin: '0 auto' }}>
			{/* ── Plan Header ── */}
			<div className="stripe-card stripe-card--elevated stripe-card--static" style={{ padding: '1.5rem', marginBottom: 16, textAlign: 'left' }}>
				{/* Badge + price row */}
				<div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
					<span
						style={{
							display: 'inline-block',
							padding: '4px 10px',
							borderRadius: 6,
							fontSize: '0.7rem',
							fontWeight: 700,
							letterSpacing: '0.08em',
							textTransform: 'uppercase',
							color: currentPlanColor.text,
							background: currentPlanColor.bg,
						}}
					>
						{planName}
					</span>
					<div style={{ textAlign: 'right', lineHeight: 1 }}>
						{isAnnual && (
							<div style={{ textDecoration: 'line-through', fontSize: '0.8rem', color: '#8898aa', marginBottom: 2 }}>
								{L.registerCheckout.perMonthStrike(pricing[planName].monthly)}
							</div>
						)}
						<span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0a2540' }}>
							${monthlyPrice}
						</span>
					</div>
				</div>

				{/* Annual total + description */}
				{isAnnual && (
					<div className="stripe-body stripe-body--xs" style={{ color: '#8898aa', marginBottom: 4 }}>
						<span style={{ fontWeight: 600, color: '#0a2540' }}>{L.registerCheckout.perYear(totalPrice)}</span>{L.registerCheckout.savePerYear((pricing[planName].monthly * 12) - pricing[planName].annual)}
					</div>
				)}
			<p className="stripe-body stripe-body--sm stripe-text-muted" style={{ margin: '0 0 14px' }}>
				{L.registerCheckout.followersLine(followerRange)}
			</p>

			{/* Annual upfront-charge notice */}
			{isAnnual && (
				<div style={{
					background: '#f8fafc',
					border: '1px solid #e2e8f0',
					borderRadius: 10,
					padding: '12px 14px',
					marginBottom: 12,
					display: 'flex',
					alignItems: 'center',
					gap: 10,
				}}>
					<i className="fa-solid fa-circle-info" style={{ color: '#475569', fontSize: '0.95rem', flexShrink: 0 }}></i>
					<span style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.4 }}>
						{L.registerCheckout.chargedToday(totalPrice + upsellTotal)}
					</span>
				</div>
			)}

			{/* Annual savings banner (when toggle hidden) */}
			{firstPackageInterval === 'annual' && isAnnual && (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
						borderRadius: 10,
						padding: '12px 16px',
						border: '1px solid #bbf7d0',
					}}
				>
					<div
						style={{
							width: 28,
							height: 28,
							borderRadius: '50%',
							background: '#dcfce7',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<i className="fa-solid fa-check" style={{ color: '#16a34a', fontSize: '0.75rem' }}></i>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#166534' }}>
							{L.registerCheckout.annualPlanSelected}
						</span>
						<span style={{ fontSize: '0.75rem', color: '#15803d' }}>
							{L.registerCheckout.savingPerYear((pricing[planName].monthly * 12) - pricing[planName].annual)}
						</span>
					</div>
				</div>
			)}

			</div>

		{/* ── Account & Profile Summary ── */}
		<div className="stripe-card stripe-card--static" style={{ padding: '1.25rem 1.5rem', marginBottom: 16, textAlign: 'left' }}>
				{/* Account email */}
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: profile ? 12 : 0 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
						<div style={{ width: 32, height: 32, borderRadius: 8, background: '#f6f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
							<i className="fa-solid fa-envelope" style={{ fontSize: '0.8rem', color: '#8898aa' }}></i>
						</div>
						<div style={{ minWidth: 0 }}>
							<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ lineHeight: 1 }}>{L.registerCheckout.accountLabel}</div>
							<div className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile.email}</div>
						</div>
					</div>
					{onChangeEmail ? (
						<button onClick={onChangeEmail} className="stripe-body stripe-body--xs" style={{ color: '#635bff', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
							{L.registerCheckout.change}
						</button>
					) : (
						<button onClick={() => setShowEmailModal(true)} className="stripe-body stripe-body--xs" style={{ color: '#635bff', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
							{L.registerCheckout.edit}
						</button>
					)}
				</div>

				{/* Linked profile */}
				{profile && profile.socials[profile.selectedProfile] && (
					<>
						<div style={{ height: 1, background: '#e3e8ee', margin: '0 -1.5rem', marginBottom: 12 }}></div>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
								<img
									src={profile.socials[profile.selectedProfile]?.photo || '/images/default.png'}
									alt=""
									style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }}
									onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
								/>
								<div>
									<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ lineHeight: 1.2 }}>
										{profile.socials[profile.selectedProfile]?.platform === 'instagram' && <>Instagram</>}
										{profile.socials[profile.selectedProfile]?.platform === 'tiktok' && <>TikTok</>}
										{profile.socials[profile.selectedProfile]?.platform === 'youtube' && <>YouTube</>}
										{profile.socials[profile.selectedProfile]?.platform === 'x' && <>X / Twitter</>}
									</div>
									<div className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 500 }}>
										@{profile.socials[profile.selectedProfile]?.username}
									</div>
								</div>
							</div>
							<button
								onClick={() => handleDeleteProfile(profile.socials[profile.selectedProfile]?.profileid)}
								className="stripe-body stripe-body--xs"
								style={{ color: '#635bff', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
							>
								{L.registerCheckout.change}
							</button>
						</div>
					</>
				)}

				{/* Profile switcher fallback */}
				{showProfileSwitcher && (
					<div style={{ paddingTop: 12 }}>
						{Object.values(profile.socials).map((social, index) => (
							<div
								key={index}
								onClick={() => selectProfile(social.profileid)}
								style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer' }}
							>
								<img src={social.photo} alt={social.username} style={{ width: 28, height: 28, borderRadius: '50%' }} onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }} />
								<span className="stripe-body stripe-body--sm">{social.username}</span>
							</div>
						))}
					</div>
				)}
				{showAddProfile && (
					<div style={{ paddingTop: 12 }}>
						<RegisterSocial complete={() => setShowAddProfile(false)} forced={true} />
					</div>
				)}
			</div>

			{/* ── Order Summary ── */}
			<div className="stripe-card stripe-card--static d-none" style={{ padding: '1.25rem 1.5rem', marginBottom: 16, textAlign: 'left' }}>
				<h3 className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540', marginBottom: 12 }}>{L.registerCheckout.orderSummary}</h3>

				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
					<span className="stripe-body stripe-body--sm stripe-text-body">
						{L.registerCheckout.planLine(planName.charAt(0).toUpperCase() + planName.slice(1), isAnnual ? L.registerCheckout.intervalAnnual : L.registerCheckout.intervalMonthly)}
					</span>
					<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>
						${totalPrice}
					</span>
				</div>

				{upsellChoices.includes("likes") && (
					<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
						<span className="stripe-body stripe-body--sm stripe-text-body">{L.registerCheckout.likesAddon}</span>
						<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>${isAnnual ? 144 : 24}</span>
					</div>
				)}
				{upsellChoices.includes("views") && (
					<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
						<span className="stripe-body stripe-body--sm stripe-text-body">{L.registerCheckout.viewsAddon}</span>
						<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>${isAnnual ? 132 : 22}</span>
					</div>
				)}
				{upsellChoices.includes("engagement") && (
					<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
						<span className="stripe-body stripe-body--sm stripe-text-body">{L.registerCheckout.engagementAddon}</span>
						<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>${isAnnual ? 108 : 17}</span>
					</div>
				)}

				{isAnnual && (
					<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
						<span className="stripe-body stripe-body--sm" style={{ color: '#1a7f4b', fontWeight: 600 }}>
							<i className="fa-solid fa-tag" style={{ marginRight: 4 }}></i> {L.registerCheckout.annualDiscount}
						</span>
						<span className="stripe-body stripe-body--sm" style={{ color: '#1a7f4b', fontWeight: 600 }}>
							-${(pricing[planName].monthly * 12) - pricing[planName].annual}
						</span>
					</div>
				)}

				<div style={{ height: 1, background: '#e3e8ee', margin: '12px 0' }}></div>

				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<span className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540' }}>
						{isAnnual ? L.registerCheckout.totalBilledToday : L.registerCheckout.dueToday}
					</span>
					<span style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0a2540' }}>
						${totalPrice + upsellTotal}
					</span>
				</div>
				{isAnnual && (
					<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ textAlign: 'right', marginTop: 2 }}>
						{L.registerCheckout.thatsPerMonth(Math.round((totalPrice + upsellTotal) / 12))}
					</div>
				)}
			</div>

			{/* ── Payment ── */}
			<div className="stripe-card stripe-card--static" style={{ padding: '1.5rem', marginBottom: 16, textAlign: 'left' }}>
				<h3 className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540', marginBottom: 16 }}>
					<i className="fa-solid fa-lock" style={{ color: '#1a7f4b', marginRight: 6, fontSize: '0.8rem' }}></i>
					{L.registerCheckout.payment}
				</h3>

				{/* Auto-renew. On = today's recurring subscription; off = a single
				    billing period, after which the plan simply ends. */}
				<div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', marginBottom: 16, borderRadius: 12, border: '1px solid #e3e8ee', background: '#f7f9fc' }}>
					<button
						type="button"
						role="switch"
						aria-checked={recurring}
						aria-label={L.registerCheckout.recurringLabel}
						onClick={() => setRecurring((v) => !v)}
						style={{
							flexShrink: 0, width: 44, height: 26, marginTop: 2, padding: 3, border: 'none', borderRadius: 999, cursor: 'pointer',
							background: recurring ? '#635bff' : '#c9d2e0', transition: 'background 0.2s ease',
						}}
					>
						<span style={{
							display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff',
							transform: recurring ? 'translateX(18px)' : 'translateX(0)', transition: 'transform 0.2s ease',
						}}></span>
					</button>
					<div style={{ minWidth: 0 }}>
						<div className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#0a2540' }}>
							{L.registerCheckout.recurringLabel}
						</div>
						<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ marginTop: 2 }}>
							{recurring
								? L.registerCheckout.recurringOnHelp(isAnnual ? L.registerCheckout.recurringIntervalYear : L.registerCheckout.recurringIntervalMonth)
								: L.registerCheckout.recurringOffHelp(isAnnual ? L.registerCheckout.recurringIntervalOneYear : L.registerCheckout.recurringIntervalOneMonth)}
						</div>
					</div>
				</div>

				<Form onSubmit={handleSubmit}>
					{/* Saved cards */}
					{Object.keys(profile.cards).length > 0 && (
						<div style={{ marginBottom: 16 }}>
							{Object.entries(profile.cards).map(([id, card]) => (
								<div
									key={id}
									onClick={() => setSelectedCard(id)}
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										padding: '12px 14px',
										borderRadius: 10,
										border: selectedCard === id ? '2px solid #635bff' : '1px solid #e3e8ee',
										background: selectedCard === id ? '#faf9ff' : '#fff',
										cursor: 'pointer',
										marginBottom: 8,
										transition: 'all 0.15s',
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
										<i className={getCardIcon(card.cardnumber)} style={{ fontSize: '1.25rem', color: '#425466' }}></i>
										<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 500 }}>
											•••• {card.cardnumber.slice(-4)}
										</span>
										<span className="stripe-body stripe-body--xs stripe-text-muted">{card.exp}</span>
									</div>
									<div style={{ width: 18, height: 18, borderRadius: '50%', border: selectedCard === id ? '5px solid #635bff' : '2px solid #cbd5e1', transition: 'all 0.15s' }}></div>
								</div>
							))}

							{/* New card option */}
							<div
								onClick={() => setSelectedCard('addCard')}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									padding: '12px 14px',
									borderRadius: 10,
									border: selectedCard === 'addCard' ? '2px solid #635bff' : '1px solid #e3e8ee',
									background: selectedCard === 'addCard' ? '#faf9ff' : '#fff',
									cursor: 'pointer',
									marginBottom: 8,
									transition: 'all 0.15s',
								}}
							>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
									<i className="fa-solid fa-plus" style={{ fontSize: '0.85rem', color: '#635bff' }}></i>
									<span className="stripe-body stripe-body--sm" style={{ fontWeight: 500, color: '#635bff' }}>{L.registerCheckout.newCard}</span>
								</div>
								<div style={{ width: 18, height: 18, borderRadius: '50%', border: selectedCard === 'addCard' ? '5px solid #635bff' : '2px solid #cbd5e1', transition: 'all 0.15s' }}></div>
							</div>
						</div>
					)}

					{/* Card inputs */}
					{(profile.cards.length == 0 || selectedCard == 'addCard') && (
						<div>
							{/* Card number */}
							<div style={{ marginBottom: 12 }}>
								<label className="stripe-body stripe-body--xs stripe-text-muted" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{L.registerCheckout.cardNumberLabel}</label>
								<div style={{ position: 'relative' }}>
									<Form.Control
										as={InputMask}
										type="text"
										ref={cardRef}
										placeholder="1234 1234 1234 1234"
										mask="9999 9999 9999 9999"
										maskChar=""
										required
										onChange={checkCardFields}
										style={{
											padding: '0.75rem 0.85rem',
											paddingRight: '3rem',
											fontSize: '0.95rem',
											borderRadius: 8,
											border: '1px solid #e3e8ee',
											boxShadow: '0 1px 2px rgba(50,50,93,0.08)',
											transition: 'border-color 0.15s, box-shadow 0.15s',
										}}
									/>
									<div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4, opacity: 0.4 }}>
										<i className="fa-brands fa-cc-visa" style={{ fontSize: '1.1rem' }}></i>
										<i className="fa-brands fa-cc-mastercard" style={{ fontSize: '1.1rem' }}></i>
										<i className="fa-brands fa-cc-amex" style={{ fontSize: '1.1rem' }}></i>
									</div>
								</div>
							</div>

							{/* Exp + CVV row */}
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
								<div>
									<label className="stripe-body stripe-body--xs stripe-text-muted" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{L.registerCheckout.expirationLabel}</label>
									<Form.Control
										as={InputMask}
										maskChar=""
										type="text"
										ref={expRef}
										placeholder={L.registerCheckout.expPlaceholder}
										mask="99/99"
										required
										onChange={(e) => { validateExpiration(e.target.value); checkCardFields(); }}
										style={{
											padding: '0.75rem 0.85rem',
											fontSize: '0.95rem',
											borderRadius: 8,
											border: `1px solid ${expError ? '#e74c3c' : '#e3e8ee'}`,
											boxShadow: expError ? '0 0 0 3px rgba(231,76,60,0.1)' : '0 1px 2px rgba(50,50,93,0.08)',
											transition: 'border-color 0.15s, box-shadow 0.15s',
										}}
									/>
									{expError && (
										<div style={{ color: '#e74c3c', fontSize: '0.75rem', marginTop: 4 }}>
											<i className="fa-solid fa-circle-exclamation" style={{ marginRight: 3 }}></i>{expError}
										</div>
									)}
								</div>
								<div>
									<label className="stripe-body stripe-body--xs stripe-text-muted" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{L.registerCheckout.cvcLabel}</label>
									<Form.Control
										as={InputMask}
										maskChar=""
										type="text"
										ref={cvvRef}
										placeholder={L.registerCheckout.cvcPlaceholder}
										mask="9999"
										required
										onChange={checkCardFields}
										style={{
											padding: '0.75rem 0.85rem',
											fontSize: '0.95rem',
											borderRadius: 8,
											border: '1px solid #e3e8ee',
											boxShadow: '0 1px 2px rgba(50,50,93,0.08)',
											transition: 'border-color 0.15s, box-shadow 0.15s',
										}}
									/>
								</div>
							</div>
						</div>
					)}

					{/* Error */}
					{functionError && (
						<div style={{
							background: '#fef2f2',
							border: '1px solid #fecaca',
							borderRadius: 10,
							padding: '12px 16px',
							marginBottom: 16,
							display: 'flex',
							alignItems: 'flex-start',
							gap: 10,
						}}>
							<i className="fa-solid fa-circle-exclamation" style={{ color: '#e74c3c', marginTop: 2 }}></i>
							<div>
								<div className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#991b1b', marginBottom: 2 }}>{L.registerCheckout.paymentFailed}</div>
								<div className="stripe-body stripe-body--xs" style={{ color: '#b91c1c' }}>{functionError}</div>
							</div>
						</div>
					)}

				{/* Submit */}
					<Button
						type="submit"
						className="stripe-btn stripe-btn--primary w-100"
						disabled={isLoading || isReinitializingCollect || !!expError}
						style={{
							padding: '0.85rem',
							fontSize: '1rem',
							marginTop: 4,
							opacity: (isLoading || isReinitializingCollect || !!expError) ? 0.6 : 1,
						}}
					>
						{isLoading ? (
							<><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }}></i>{L.registerCheckout.processing}</>
						) : (
							<>{L.registerCheckout.startMyGrowth} <i className="fa-solid fa-arrow-right" style={{ marginLeft: 8 }}></i></>
						)}
					</Button>

					{isReinitializingCollect && (
						<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ textAlign: 'center', marginTop: 12 }}>
							<i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>
							{L.registerCheckout.loadingPaymentOptions}
						</div>
					)}

					{/* Apple/Google Pay */}
					<div style={{ marginTop: 16 }}>
						<div id="googlepaybutton"></div>
						{window.location.hostname !== 'localhost' && <div id="applepaybutton"></div>}
					</div>
				</Form>
			</div>

			{/* ── Trust footer ── */}
			<div style={{ padding: '14px 0 1.5rem', textAlign: 'center' }}>
				{/* Trust text */}
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, color: '#8898aa', fontSize: '0.78rem', marginBottom: 10 }}>
					<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
						<i className="fa-solid fa-lock" style={{ fontSize: '0.65rem', color: '#1a7f4b' }}></i>
						{L.registerCheckout.secureCheckout}
					</span>
					<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
						<i className="fa-solid fa-times-circle" style={{ fontSize: '0.65rem', color: '#8898aa' }}></i>
						{L.registerCheckout.cancelAnytime}
					</span>
					<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
						<i className="fa-solid fa-shield-halved" style={{ fontSize: '0.65rem', color: '#8898aa' }}></i>
						{L.registerCheckout.guarantee}
					</span>
				</div>
				{/* Card logos */}
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
					<i className="fa-brands fa-cc-visa" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
					<i className="fa-brands fa-cc-mastercard" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
					<i className="fa-brands fa-cc-amex" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
					<i className="fa-brands fa-cc-discover" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
				</div>
			</div>

			{/* ── Email Modal ── */}
			{showEmailModal && (
				<Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered>
					<Modal.Header closeButton style={{ border: 'none', padding: '1.25rem 1.5rem 0' }}>
						<Modal.Title className="stripe-heading stripe-heading--xs">{L.registerCheckout.changeEmailTitle}</Modal.Title>
					</Modal.Header>
					<Modal.Body style={{ padding: '1rem 1.5rem 1.5rem' }}>
						<label className="stripe-body stripe-body--xs stripe-text-muted" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{L.registerCheckout.newEmailLabel}</label>
						<Form.Control
							type="email"
							value={email}
							onChange={handleEmailChange}
							placeholder={L.registerCheckout.emailPlaceholder}
							style={{ padding: '0.75rem 0.85rem', borderRadius: 8, border: '1px solid #e3e8ee' }}
						/>
					</Modal.Body>
					<Modal.Footer style={{ border: 'none', padding: '0 1.5rem 1.25rem' }}>
						<Button variant="light" onClick={() => setShowEmailModal(false)} style={{ borderRadius: 8 }}>{L.registerCheckout.cancel}</Button>
						<Button className="stripe-btn stripe-btn--primary" onClick={handleEmailSubmit} style={{ padding: '0.5rem 1.25rem' }}>{L.registerCheckout.save}</Button>
					</Modal.Footer>
				</Modal>
			)}
		</div>
	);
};

export default Checkout;
